import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  Gift,
  Activity,
  Calendar,
  RefreshCw,
  MoreVertical,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  MousePointer,
  CreditCard,
  AlertCircle,
  ShoppingCart,
  UserPlus,
  FileText,
  ShieldAlert,
  CheckCircle,
  Star,
  MessageSquare,
  UserCog,
  Smartphone
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { store } from '../redux/store';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/Loading.json';
import {
  fetchCampaignAnalytics,
  fetchCampaignCTR,
  syncDashboard,
  updateKPI,
  addRecentActivity,
  setActiveUsersNow,
  fetchRatings
} from '../redux/slices/dashboardSlice';
import { useSocket } from '../context/SocketContext';
import { fetchUsers } from '../redux/slices/userSlice';
import { PERMISSIONS, usePermissionHook } from '../utils/permissions';
import { getDefaultPageForUser } from '../utils/navigationHelper';
import ActivityFeed from '../components/ActivityFeed';
import CampaignTypeBadge from '../components/CampaignTypeBadge';
import { mapDashboardDateRangeToStats, resolveCampaignPersonalized } from '../utils/campaignTypeDisplay';
import { Dropdown } from '../components/ui/dropdown-01';
import ActivityStatsWidget from '../components/ActivityStatsWidget';
import { ratingService } from '../services/ratingService';
import { activityService } from '../services/activityService';
import { extractActivityEventFromSocketPayload } from '../utils/activitySocketPayload';

const TR_MONTH_ABBR = {
  ocak: 'Oca',
  şubat: 'Şub',
  subat: 'Şub',
  mart: 'Mar',
  nisan: 'Nis',
  mayıs: 'May',
  mayis: 'May',
  haziran: 'Haz',
  temmuz: 'Tem',
  ağustos: 'Ağu',
  agustos: 'Ağu',
  eylül: 'Eyl',
  eylul: 'Eyl',
  ekim: 'Eki',
  kasım: 'Kas',
  kasim: 'Kas',
  aralık: 'Ara',
  aralik: 'Ara',
};

function normalizeFilterDateRange(value) {
  switch (value) {
    case 'today':
    case 'daily':
      return 'today';
    case 'week':
    case 'weekly':
      return 'week';
    case 'month':
    case 'monthly':
      return 'month';
    case 'year':
    case 'yearly':
      return 'year';
    case 'all':
      return 'all';
    default:
      return value || '';
  }
}

function dashboardFiltersMatch(payloadFilter, currentFilter) {
  if (!payloadFilter) return true;
  const payloadRange = normalizeFilterDateRange(payloadFilter.dateRange);
  const currentRange = normalizeFilterDateRange(currentFilter.dateRange);
  return (
    payloadRange === currentRange
    && String(payloadFilter.dateFrom || '') === String(currentFilter.dateFrom || '')
    && String(payloadFilter.dateTo || '') === String(currentFilter.dateTo || '')
  );
}

/** Grafik X ekseni — uzun ay+yıl etiketlerini kısaltır (örn. Haziran 2025 → Haz '25) */
function formatUserGrowthAxisLabel(label, dateRange) {
  if (!label || typeof label !== 'string') return label;
  if (dateRange !== 'yearly' && dateRange !== 'monthly') return label;

  const trimmed = label.trim();

  // YYYY-MM veya YYYY-MM-DD
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})(?:-\d{2})?$/);
  if (isoMatch) {
    const year = isoMatch[1];
    const monthIndex = Number(isoMatch[2]) - 1;
    const monthNames = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    const shortMonth = monthNames[monthIndex] || isoMatch[2];
    return dateRange === 'yearly' ? `${shortMonth} '${year.slice(-2)}` : `${shortMonth} ${year}`;
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    const monthKey = parts[0].toLocaleLowerCase('tr-TR');
    const yearPart = parts[parts.length - 1];
    const shortMonth = TR_MONTH_ABBR[monthKey] || parts[0].slice(0, 3);
    const shortYear = /^\d{4}$/.test(yearPart) ? `'${yearPart.slice(-2)}` : yearPart;
    return dateRange === 'yearly' ? `${shortMonth} ${shortYear}` : `${shortMonth} ${yearPart}`;
  }

  return trimmed.length > 10 ? `${trimmed.slice(0, 9)}…` : trimmed;
}

function mapFrontendDateRangeToBackend(range) {
  switch (range) {
    case 'today':
      return 'today';
    case 'weekly':
      return 'week';
    case 'monthly':
      return 'month';
    case 'yearly':
      return 'year';
    case 'all':
      return 'all';
    default:
      return 'week';
  }
}

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Dashboard tarih filtresini oturum boyunca sakla (sayfadan ayrılıp dönünce koru);
  // yeni oturumda (yeni sekme/tarayıcı) her zaman 'today' ile açılır
  const getStoredDateRange = () => {
    if (typeof window === 'undefined') return 'today';
    const stored = sessionStorage.getItem('dashboard_dateRange');
    const valid = ['today', 'weekly', 'monthly'];
    if (stored === 'yearly' || stored === 'all') return 'monthly';
    return stored && valid.includes(stored) ? stored : 'today';
  };
  const [dateRange, setDateRange] = useState(() => getStoredDateRange());
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const isFetchingRef = useRef(false);
  const hasInitialLoadRef = useRef(false);
  const lastSyncTimestampRef = useRef(0);
  const dashboardStatsRef = useRef(null);
  const [ageGroups, setAgeGroups] = useState({});
  const [genderStats, setGenderStats] = useState({});
  const [demographicsLoading, setDemographicsLoading] = useState(true);

  // WebSocket Entegrasyonu
  const { socket, isConnected } = useSocket();

  // İzin kontrolleri
  // İzin kontrolleri
  const canViewDashboard = usePermissionHook(PERMISSIONS.DASHBOARD_VIEW);
  const { user } = useSelector((state) => state.auth);

  // Sayfa geçişlerini takip et (performans testi için)
  useEffect(() => {
    const startTime = performance.now();
    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
    };
  }, [location.pathname]);

  /**
   * Tarih filtresi değerlerini hesapla ve backend formatına çevir
   * Frontend dateRange: 'today' | 'weekly' | 'monthly' | 'yearly' | 'all'
   * Backend dateRange: 'today' | 'week' | 'month' | 'year' | null
   * 
   * Tarih hesaplama mantığı (backend ile uyumlu — bugün dahil kayan pencere):
   * - today: Bugün (00:00:00 - 23:59:59)
   * - weekly: Son 7 gün (bugün dahil)
   * - monthly: Son 30 gün (bugün dahil)
   * - yearly: Son 365 gün (bugün dahil)
   */
  const getDashboardFilter = useCallback(() => {
    // Timezone-safe tarih formatlama - Local timezone kullan
    const formatDateLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const startOfDay = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const subtractDaysFromStart = (date, daysBack) => {
      const d = startOfDay(date);
      d.setDate(d.getDate() - daysBack);
      return d;
    };
    
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    
    // Bugünün sonu (23:59:59.999) - Local timezone
    const todayEnd = new Date(year, month, day, 23, 59, 59, 999);
    const dateTo = formatDateLocal(todayEnd);
    
    let dateFrom;
    let backendDateRange;
    
    switch (dateRange) {
      case 'today':
        // Bugün (00:00:00 - 23:59:59) - Local timezone
        const todayStart = new Date(year, month, day, 0, 0, 0, 0);
        dateFrom = formatDateLocal(todayStart);
        backendDateRange = 'today';
        break;
      case 'weekly':
        dateFrom = formatDateLocal(subtractDaysFromStart(now, 6));
        backendDateRange = 'week';
        break;
      case 'monthly':
        dateFrom = formatDateLocal(subtractDaysFromStart(now, 29));
        backendDateRange = 'month';
        break;
      case 'yearly':
        dateFrom = formatDateLocal(subtractDaysFromStart(now, 364));
        backendDateRange = 'year';
        break;
      case 'all':
        dateFrom = null;
        backendDateRange = null;
        break;
      default:
        dateFrom = null;
        backendDateRange = null;
    }
    
    const filterResult = {
      dateFrom,
      dateTo,
      dateRange: backendDateRange
    };
    
    return filterResult;
  }, [dateRange]);

  const activityFeedDateFilter = useMemo(() => {
    const filter = getDashboardFilter();
    return {
      dateFrom: filter.dateFrom,
      dateTo: filter.dateFrom ? filter.dateTo : null,
    };
  }, [getDashboardFilter]);

  // Filtre seçimini oturum boyunca sakla (sayfadan ayrılıp dönünce koru)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('dashboard_dateRange', dateRange);
    }
  }, [dateRange]);

  // Error mesajını string'e çevir
  const getErrorMessage = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.error) return error.error;
    if (typeof error === 'object') {
      // Obje ise önemli alanları kontrol et
      if (error.success !== undefined && error.message) {
        return error.message;
      }
      return JSON.stringify(error);
    }
    return 'Bilinmeyen hata';
  };

  // Redux state
  const {
    dashboardStats,
    realtimeData,
    realtimeLoading,
    realtimeError,
    campaignAnalytics,
    campaignAnalyticsLoading,
    campaignSectionsLoading,
    campaignSectionsSettled,
    campaignAnalyticsError,
    userBehavior,
    userBehaviorLoading,
    userBehaviorError,
    platformDistribution,
    appVersionDistribution,
    campaignCTR,
    campaignCTRLoading,
    campaignCTRError,
    recentActivities,
    totalUsers: reduxTotalUsers,
    newUsers: reduxNewUsers
  } = useSelector(state => state.dashboard);
  const {
    ratings,
    ratingsPagination,
    ratingsLoading,
    ratingsError,
  } = useSelector(state => state.dashboard);
  
  useEffect(() => {
    dashboardStatsRef.current = dashboardStats;
  }, [dashboardStats]);

  // ✅ KPI verileri - dashboardStats.kpi'den geliyor (KPI endpoint'inden)
  const kpiData = dashboardStats?.kpi || {};
  const kpiSessions = kpiData.sessions ?? dashboardStats?.sessions ?? 0;
  // ✅ totalUsers öncelik sırası: dashboardStats.kpi.totalUsers > totalUsers state > 0
  const kpiTotalUsers = kpiData.totalUsers ?? reduxTotalUsers ?? 0;
  const kpiNewUsers = kpiData.newUsers ?? 0;
  const kpiScreenViews = kpiData.screenViews ?? 0;

  // Ham KPI verisini loglama kaldırıldı

  // Kullanıcı verileri
  const { users, loading: usersLoading, error: usersError } = useSelector(state => state.users);
  const usersCount = users?.length || 0;

  // İsmi capitalize et (Türkçe karakterlere uygun: i→İ, ı→I)
  const capitalizeName = (name) => {
    if (!name) return name;
    return name
      .toLocaleLowerCase('tr-TR')
      .split(' ')
      .map(word => word ? word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR') : '')
      .join(' ');
  };

  // Yorum için görüntülenecek ismi al - TC'ye karşılık gelen ismi kullanıcı listesinden bul
  const getRatingUserRecord = (rating) => {
    const tc = rating.identityNum || rating.tcNo || rating.tc;
    if (tc && users?.length) {
      return users.find(
        (u) =>
          String(u.tc) === String(tc) ||
          String(u.id) === String(tc) ||
          String(u.id) === String(rating.userId)
      );
    }
    if (rating.userId && users?.length) {
      return users.find(
        (u) => String(u.id) === String(rating.userId) || String(u.tc) === String(rating.userId)
      );
    }
    return null;
  };

  const getRatingRouteId = (rating) => {
    const user = getRatingUserRecord(rating);
    return user?.tc || user?.id || rating.identityNum || rating.tcNo || rating.tc || rating.userId || null;
  };

  const getRatingDisplayName = (rating) => {
    const name = rating.customerName
      || rating.fullName
      || (rating.firstName && rating.lastName ? `${rating.firstName} ${rating.lastName}`.trim() : null)
      || rating.firstName
      || rating.lastName;
    if (name) return capitalizeName(name);
    const tc = rating.identityNum || rating.tcNo || rating.tc;
    if (tc && users?.length) {
      const user = users.find(u => String(u.tc) === String(tc));
      if (user?.name && user.name !== '-') return capitalizeName(user.name);
    }
    return 'Kullanıcı';
  };
  
  // ✅ KPI unified — newUsers (sp_YKSadakatKullaniciDashboard)
  const newUsersCount = kpiNewUsers ?? reduxNewUsers ?? 0;

  const userDateDebug = React.useMemo(() => {
    if (!Array.isArray(users) || users.length === 0) {
      return {
        totalUsersInList: 0,
        validRegisterDateCount: 0,
        invalidRegisterDateCount: 0,
        inRangeCount: 0,
        sample: [],
      };
    }

    const now = new Date();
    let startDate = null;
    switch (dateRange) {
      case 'today': {
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
        break;
      }
      case 'weekly': {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
        break;
      }
      case 'monthly': {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
        startDate.setHours(0, 0, 0, 0);
        break;
      }
      case 'yearly': {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 364);
        startDate.setHours(0, 0, 0, 0);
        break;
      }
      case 'all':
      default:
        startDate = null;
    }

    let validRegisterDateCount = 0;
    let invalidRegisterDateCount = 0;
    let inRangeCount = 0;

    const sample = users.slice(0, 25).map((user) => {
      const raw = user?.registerDate;
      let parsed = null;
      if (raw && typeof raw === 'string' && raw.includes('.')) {
        const [day, month, year] = raw.split('.');
        if (day && month && year) {
          parsed = new Date(`${year}-${month}-${day}`);
        }
      } else if (raw) {
        parsed = new Date(raw);
      }

      const isValid = parsed instanceof Date && !isNaN(parsed.getTime());
      if (isValid) validRegisterDateCount += 1;
      else invalidRegisterDateCount += 1;

      const inRange = isValid
        ? (dateRange === 'all' ? parsed <= now : parsed >= startDate && parsed <= now)
        : false;
      if (inRange) inRangeCount += 1;

      return {
        id: user?.id,
        tc: user?.tc,
        rawRegisterDate: raw,
        parsedISO: isValid ? parsed.toISOString() : null,
        inRange,
      };
    });

    // sample dışındaki kullanıcıları da saymaya dahil et
    if (users.length > sample.length) {
      users.slice(sample.length).forEach((user) => {
        const raw = user?.registerDate;
        let parsed = null;
        if (raw && typeof raw === 'string' && raw.includes('.')) {
          const [day, month, year] = raw.split('.');
          if (day && month && year) {
            parsed = new Date(`${year}-${month}-${day}`);
          }
        } else if (raw) {
          parsed = new Date(raw);
        }
        const isValid = parsed instanceof Date && !isNaN(parsed.getTime());
        if (isValid) {
          validRegisterDateCount += 1;
          const inRange = dateRange === 'all' ? parsed <= now : parsed >= startDate && parsed <= now;
          if (inRange) inRangeCount += 1;
        } else {
          invalidRegisterDateCount += 1;
        }
      });
    }

    return {
      totalUsersInList: users.length,
      validRegisterDateCount,
      invalidRegisterDateCount,
      inRangeCount,
      sample,
    };
  }, [users, dateRange]);
  // ✅ Kampanya Performansı verileri - Backend'den gelen yeni format
  // Backend formatı: { campaignId, impressions, clicks, ctr, isPersonalized }
  const popularCampaigns = React.useMemo(() => {
    // Önce campaignAnalytics.popularCampaigns'i kontrol et
    let campaigns = Array.isArray(campaignAnalytics?.popularCampaigns)
    ? campaignAnalytics.popularCampaigns
    : [];
    
    
    // ✅ Yeni backend formatı: { campaignId, impressions, clicks, ctr, isPersonalized }
    // Chart'ın beklediği format: { campaignName, impressions, clicks, ctr }
    campaigns = campaigns.map((campaign, index) => {
      // Campaign ID - Backend'den gelen zorunlu alan
      const campaignId = campaign.campaignId || `KAMPANYA_${index + 1}`;
      
      // Campaign Name - Backend'den campaignTitle geliyorsa onu kullan
      const campaignName = campaign.campaignTitle 
        || campaign.campaignName 
        || campaign.name 
        || campaign.title 
        || null; // campaignId kullanmayalım, backend'den gelecek
      
      // Impressions - Backend'den gelen zorunlu alan
      const impressions = campaign.impressions ?? 0;
      
      // Clicks - Backend'den gelen zorunlu alan
      const clicks = campaign.clicks ?? 0;
      
      // CTR - Backend'den geliyorsa kullan, yoksa hesapla: (clicks / impressions) * 100
      const ctr = campaign.ctr !== undefined 
        ? campaign.ctr 
        : (impressions > 0 ? ((clicks / impressions) * 100) : 0);
      
      // isPersonalized - Backend'den gelen; campaign_0_* ve katalog ile düzelt
      const isPersonalized = resolveCampaignPersonalized(campaign);
      
      // Chart'ın beklediği standart formata çevir
      const formattedCampaign = {
        campaignId: campaignId,
        campaignName: campaignName,
        impressions: impressions,
        clicks: clicks,
        ctr: ctr,
        isPersonalized: isPersonalized,
      };
      
      
      return formattedCampaign;
    });
    
    // En çok tıklanan 5 kampanyayı tut (clicks'a göre sırala)
    campaigns.sort((a, b) => (b.clicks || 0) - (a.clicks || 0));
    campaigns = campaigns.slice(0, 5);
    
    
    return campaigns;
  }, [campaignAnalytics?.popularCampaigns]);
  /**
   * Kampanya Etkileşimi: Sadece kampanya detail view'ları (tıklamaları)
   * Öncelik: CTR endpoint'inden detailViews (kampanya tıklamaları)
   * Fallback: 0
   */
  const campaignInteractionCount = React.useMemo(() => {
    // ✅ Kampanya etkileşimi = Sadece kampanya detail view'ları (tıklamaları)
    // campaignCTR.total.detailViews = Genel + Kişiselleştirilmiş kampanya tıklamalarının toplamı
    if (campaignCTR?.total?.detailViews !== undefined) {
      return campaignCTR.total.detailViews;
    }
    return 0;
  }, [campaignCTR]);
  
  // ✅ Aktif Kullanıcı: Tarih aralığında aktivite yapan kullanıcılar
  // activeUsersInRange: Veritabanından (tarih aralığında login olanlar) - kpiData.activeUsers'da
  // activeUsersNow: WebSocket'ten (gerçek zamanlı bağlantılar) - realtimeData.activeUsers'da
  const activeUsers24Saat = campaignAnalytics?.activeUsers ?? kpiData.activeUsersInRange ?? kpiData.activeUsers ?? 0; // Tarih aralığında aktif
  const activeUsersNow = realtimeData?.activeUsers ?? 0; // Şu an aktif (WebSocket'ten)
  
  // ✅ Kampanya Özeti - WebSocket'ten anlık güncellenir
  const campaignSummary = {
    activeUsers: activeUsers24Saat, // ✅ Son 24 saatte aktivite yapan kullanıcılar (WebSocket'ten güncellenir)
    totalEvents: kpiData.totalEvents ?? 0, // ✅ Toplam event sayısı (WebSocket'ten güncellenir) - TÜM event'ler
    campaignInteractions: campaignInteractionCount, // ✅ Kampanya etkileşimi (sadece kampanya tıklamaları)
    totalCampaigns: campaignAnalytics?.totalCampaigns ?? kpiData.totalCampaigns ?? 0, // ✅ Toplam kampanya sayısı (WebSocket'ten güncellenir)
  };
  const campaignSummaryCards = [
    { label: 'Aktif Kullanıcı', value: campaignSummary.activeUsers }, // ✅ Son 24 saatte aktivite yapanlar
    { label: 'Toplam Event', value: campaignSummary.totalEvents },
    { label: 'Takip Edilen Kampanya', value: campaignSummary.totalCampaigns },
  ];
  

  // ✅ TÜM İSTATİSTİK VERİLERİNİ LOGLA
  useEffect(() => {
    
    // 1. KPI VERİLERİ (Ana Göstergeler)
    
    // 2. KAMPANYA ANALİTİKLERİ
    if (campaignAnalytics?.popularCampaigns?.length > 0) {
      campaignAnalytics.popularCampaigns.forEach((campaign, index) => {
      });
    }
    
    // 3. KULLANICI İSTATİSTİKLERİ
    
    // 4. KULLANICI BÜYÜMESİ (User Growth)
    const userGrowthDebugSeries = dashboardStats?.charts?.userGrowth || [];
    if (userGrowthDebugSeries.length > 0) {
      userGrowthDebugSeries.slice(0, 5).forEach((item, index) => {
      });
      if (userGrowthDebugSeries.length > 5) {
      }
    } else {
    }
    
    // 5. PLATFORM DAĞILIMI (iOS/Android)
    if (platformDistribution) {
      const total = platformDistribution.total || (platformDistribution.ios + platformDistribution.android + platformDistribution.other);
    } else {
    }
    
    // 6. KAMPANYA CTR ANALİZİ (Click-Through Rate)
    if (campaignCTR) {
      
      
    } else {
    }
    
    // 7. REAL-TIME VERİLER (WebSocket)
    if (realtimeData) {
      if (realtimeData.events && realtimeData.events.length > 0) {
        realtimeData.events.slice(0, 3).forEach((event, index) => {
        });
      }
    } else {
    }
    
    // 8. KULLANICI DAVRANIŞI (User Behavior)
    if (userBehavior) {
    } else {
    }
    
    // 9. SON AKTİVİTELER (Recent Activities)
    if (recentActivities && recentActivities.length > 0) {
      recentActivities.slice(0, 5).forEach((activity, index) => {
      });
    } else {
    }
    
    // 10. KAMPANYA ÖZETİ
    
    // 11. POPÜLER KAMPANYALAR (Chart için hazırlanmış)
    if (popularCampaigns && popularCampaigns.length > 0) {
      popularCampaigns.forEach((campaign, index) => {
      });
    } else {
    }
    
    // 12. TARİH ARALIĞI BİLGİSİ
    
    // 13. HATA DURUMLARI
    const errors = [];
    if (realtimeError) errors.push({ kaynak: 'Real-time Veriler', hata: realtimeError });
    if (campaignAnalyticsError) errors.push({ kaynak: 'Kampanya Analizleri', hata: campaignAnalyticsError });
    if (userBehaviorError) errors.push({ kaynak: 'Kullanıcı Davranışı', hata: userBehaviorError });
    if (usersError) errors.push({ kaynak: 'Kullanıcı Listesi', hata: usersError });
    if (campaignCTRError) errors.push({ kaynak: 'Kampanya CTR', hata: campaignCTRError });
    
    if (errors.length > 0) {
      errors.forEach(error => {
      });
    } else {
    }
    
    // 14. LOADING DURUMLARI
    
    // 15. TAM STATE ÖZETİ
    
  }, [
    // Tüm istatistik verilerini izle
    kpiSessions,
    kpiTotalUsers,
    kpiNewUsers,
    kpiScreenViews,
    activeUsers24Saat,
    kpiData,
    campaignAnalytics,
    usersCount,
    newUsersCount,
    dateRange,
    dashboardStats?.charts?.userTrend,
    platformDistribution,
    campaignCTR,
    campaignCTRLoading,
    campaignInteractionCount,
    realtimeData,
    realtimeLoading,
    realtimeError,
    userBehavior,
    userBehaviorLoading,
    userBehaviorError,
    recentActivities,
    campaignSummary,
    popularCampaigns,
    lastUpdate,
    usersLoading,
    campaignAnalyticsLoading,
    usersError,
    campaignCTRError,
    dashboardStats
  ]);

  const usersCountRef = useRef(0);
  
  // usersCount'u ref'te tut ki dependency array'de olmasın
  useEffect(() => {
    usersCountRef.current = usersCount;
  }, [usersCount]);

  // Kullanıcı listesi boşsa bir kez kullanıcıları çek (yorumlarda isim gösterebilmek için)
  useEffect(() => {
    if (!users || users.length === 0) {
      dispatch(fetchUsers({ force: false }));
    }
  }, [dispatch, users]);

  // Yorumları çek
  useEffect(() => {
    const filter = getDashboardFilter();
    dispatch(fetchRatings({
      page: 1,
      limit: 5,
      dateRange: filter.dateRange,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      force: true, // tarih aralığı değişince güncelle
    })).catch((error) => {
      // Hata sessizce handle ediliyor - Redux slice'da zaten rejectWithValue ile handle ediliyor
      // Burada sadece unhandled rejection'ı önlemek için catch ekliyoruz
    });
  }, [dateRange, dispatch, getDashboardFilter]);

  // ✅ Yaş/cinsiyet — KPI unified activitiesStats.summary (SP raporu)
  useEffect(() => {
    setDemographicsLoading(true);
  }, [dateRange]);

  useEffect(() => {
    if (campaignAnalyticsLoading) {
      return;
    }

    const summary = dashboardStats?.activitiesStats?.summary;
    if (summary) {
      setAgeGroups(summary.ageGroups || {});
      setGenderStats(summary.gender || {});
    } else {
      setAgeGroups({});
      setGenderStats({});
    }
    setDemographicsLoading(false);
  }, [dashboardStats?.activitiesStats, campaignAnalyticsLoading, dateRange]);

  /**
   * İlk yükleme için gerekli verileri çek
   * Rehbere göre sadece:
   * 1. KPI verileri (ZORUNLU)
   * 2. User Growth Chart (OPSİYONEL)
   * 
   * Diğer veriler WebSocket'ten geliyor, gereksiz REST API çağrıları yapılmıyor
   */
  const fetchInitialDashboardData = useCallback(async ({ force = false } = {}) => {
    if (isFetchingRef.current && !force) {
      return;
    }

    isFetchingRef.current = true;
    const fetchStartTime = performance.now();
    
    try {
      const tasks = [];

        // Tarih aralığını backend formatına çevir
      // Frontend: 'today' | 'weekly' | 'monthly' | 'yearly' | 'all'
      // Backend: 'today' | 'week' | 'month' | 'year' | 'all'
      let backendDateRange;
        switch (dateRange) {
          case 'today':
            backendDateRange = 'today';
            break;
          case 'weekly':
            backendDateRange = 'week';
            break;
          case 'monthly':
            backendDateRange = 'month';
            break;
          case 'yearly':
            backendDateRange = 'year';
            break;
          case 'all':
            backendDateRange = 'all';
            break;
          default:
            backendDateRange = 'week'; // Varsayılan
        }
        
      // ✅ 1. KPI core — kartlar, userGrowth, demografi (önce)
      try {
        await dispatch(fetchCampaignAnalytics({
          dateRange: backendDateRange,
          force,
          sections: 'core',
        })).unwrap();
      } catch (error) {
        // ConditionError normal bir durumdur (cache'deki veri yeterince yeni)
      }

      // ✅ 2. KPI campaigns + CTR — arka planda (UI skeleton)
      try {
        await dispatch(fetchCampaignAnalytics({
          dateRange: backendDateRange,
          force,
          sections: 'campaigns',
        })).unwrap();
      } catch (error) {
        // ConditionError veya ağ hatası — UI settled state ile boş gösterir
      }

      const optionalTasks = [
        dispatch(fetchCampaignCTR({ dateRange: backendDateRange, force })),
      ];

      // Opsiyonel verileri paralel çek (hata olsa bile devam et)
      if (optionalTasks.length > 0) {
        await Promise.allSettled(
          optionalTasks.map(async (task) => {
            try {
              await task.unwrap();
              return null;
            } catch (error) {
              // ConditionError normal bir durumdur (cache'deki veri yeterince yeni)
              return null;
            }
          })
        );
      }

      // Hatalar zaten yukarıda handle edildi

      const fetchEndTime = performance.now();
      const fetchDuration = fetchEndTime - fetchStartTime;

      setLastUpdate(new Date());
    } catch (error) {
      // Hata handle edildi
    } finally {
      isFetchingRef.current = false;
    }
  }, [dispatch, dateRange, getDashboardFilter]);

  // NOT: startRealtimeInterval ve stopRealtimeInterval kaldırıldı
  // Interval artık useEffect içinde doğrudan yönetiliyor

  /**
   * WebSocket: analytics:event event'ini dinle (Anlık güncellemeler)
   * Her yeni event geldiğinde KPI'ları delta ile günceller
   */
  useEffect(() => {
    if (!socket || !canViewDashboard) return;

    const handleAnalyticsEvent = (eventData) => {
      const event = extractActivityEventFromSocketPayload(eventData);
      if (!event) return;

      if (eventData.kpiDelta) {
        dispatch(updateKPI({ kpiDelta: eventData.kpiDelta, event }));
      }

      dispatch(addRecentActivity(event));
      setLastUpdate(new Date());
    };

    socket.on('analytics:event', handleAnalyticsEvent);

    return () => {
      socket.off('analytics:event', handleAnalyticsEvent);
    };
  }, [socket, canViewDashboard, dispatch]);

  /**
   * WebSocket: dashboard:active-users — anlık aktif sayacının TEK kaynağı
   * dashboard:sync KPI içindir; activeUsersNow oradan okunmaz.
   */
  useEffect(() => {
    if (!socket || !canViewDashboard) return;

    const handleActiveUsers = (data) => {
      if (typeof data?.activeUsersNow === 'number') {
        dispatch(setActiveUsersNow(data.activeUsersNow));
        setLastUpdate(new Date());
      }
    };

    socket.on('dashboard:active-users', handleActiveUsers);

    return () => {
      socket.off('dashboard:active-users', handleActiveUsers);
    };
  }, [socket, canViewDashboard, dispatch]);

  /**
   * WebSocket: dashboard:sync — KPI / grafik senkronu (anlık aktif sayacı DEĞİL)
   */
  useEffect(() => {
    if (!socket || !canViewDashboard) return;

    const handleDashboardSync = (eventData) => {
      if (eventData.type === 'DASHBOARD_SYNC' && eventData.data) {
        const payloadTimestamp = eventData.serverTimestamp || eventData.data.serverTimestamp || 0;
        const payloadFilter = eventData.filterContext || eventData.data.filterContext || null;
        const currentFilter = getDashboardFilter();
        const isFilterMatch = dashboardFiltersMatch(payloadFilter, currentFilter);

        if (payloadTimestamp && payloadTimestamp < lastSyncTimestampRef.current) {
          return;
        }
        if (!isFilterMatch) {
          return;
        }
        if (payloadTimestamp) {
          lastSyncTimestampRef.current = payloadTimestamp;
        }

        // userGrowth öncelikli, fallback userTrend
        const incomingGrowth = eventData.data.charts?.userGrowth;
        const incomingTrend = eventData.data.charts?.userTrend;
        const incomingSeries = (incomingGrowth && incomingGrowth.length > 0) ? incomingGrowth : incomingTrend;

        const statsSnapshot = dashboardStatsRef.current;
        const currentGrowthLength = statsSnapshot?.charts?.userGrowth?.length || 0;
        const currentTrendLength = statsSnapshot?.charts?.userTrend?.length || 0;
        const currentSeriesLength = currentGrowthLength > 0 ? currentGrowthLength : currentTrendLength;

        if (
          Array.isArray(incomingSeries) &&
          incomingSeries.length > 0 &&
          currentSeriesLength > 0 &&
          incomingSeries.length < currentSeriesLength
        ) {
          // Yalnızca kullanıcı serisini (growth/trend) mevcut veriyle değiştir, diğer sync verilerini kaybetme
          eventData = {
            ...eventData,
            data: {
              ...eventData.data,
              charts: {
                ...eventData.data.charts,
                userGrowth: statsSnapshot?.charts?.userGrowth || incomingGrowth,
                userTrend: statsSnapshot?.charts?.userTrend || incomingTrend,
              },
            },
          };
        }
        // Tüm dashboard verilerini sync et (fallback için)
        const syncScope = eventData.syncScope || eventData.data?.syncScope || 'core';
        dispatch(syncDashboard({
          syncData: eventData.data,
          syncScope,
          updateActiveUsersNow: false,
        }));
        setLastUpdate(new Date());
      }
    };

    socket.on('dashboard:sync', handleDashboardSync);

    return () => {
      socket.off('dashboard:sync', handleDashboardSync);
    };
  }, [socket, canViewDashboard, dispatch, getDashboardFilter]);

  /**
   * WebSocket bağlantısı kurulduğunda ve tarih filtresi değiştiğinde
   * Backend'e tarih filtresi bilgisini gönder
   * Backend bu filtreye göre dashboard:sync verilerini çekecek
   */
  useEffect(() => {
    if (!socket || !canViewDashboard) return;

      // Socket bağlantısını bekle
      const sendFilter = () => {
        const filter = getDashboardFilter();
        socket.emit('set-dashboard-filter', filter);
      };

    // Socket bağlıysa direkt gönder
    if (socket.connected) {
      sendFilter();
      } else {
      // Socket bağlanana kadar bekle
      const handleConnect = () => {
        sendFilter();
      };
      
      socket.once('connect', handleConnect);
      
      return () => {
        socket.off('connect', handleConnect);
      };
    }
  }, [socket, canViewDashboard, getDashboardFilter]);

  /**
   * WebSocket: dashboard-filter-set event'ini dinle
   * Backend'den filtre ayarlandığına dair onay gelir
   */
  useEffect(() => {
    if (!socket || !canViewDashboard) return;

    const handleFilterSet = () => {};

    socket.on('dashboard-filter-set', handleFilterSet);

    return () => {
      socket.off('dashboard-filter-set', handleFilterSet);
    };
  }, [socket, canViewDashboard]);

  /**
   * İlk yükleme - Cache kontrolü ile REST API çağrısı yap
   * Sonrasında tüm güncellemeler WebSocket'ten gelecek
   * 
   * ✅ Cache mekanizması ile gereksiz API çağrılarını önler
   * ✅ dateRange değiştiğinde ÇALIŞMAZ (ayrı useEffect var)
   * ✅ 30 saniyede bir ÇALIŞMAZ (dashboard:sync yeterli)
   * ✅ Sayfa geri geldiğinde cache varsa API çağrısı yapmaz
   */
  useEffect(() => {
    if (!canViewDashboard) {
      return;
    }

    // ✅ Redux state'teki cache'i kontrol et (core / campaigns ayrı)
    const state = store.getState();
    const dashboardState = state.dashboard;
    const CACHE_TTL = 5 * 60 * 1000;
    const backendDateRange = mapFrontendDateRangeToBackend(dateRange);

    const lastCoreFetched =
      dashboardState?.lastFetched?.kpiCore
      ?? dashboardState?.lastFetched?.campaignAnalytics;
    const lastCampaignsFetched = dashboardState?.lastFetched?.kpiCampaigns;
    const hasCoreData = dashboardState?.dashboardStats?.kpi != null;
    const coreCacheAge = lastCoreFetched ? Date.now() - lastCoreFetched : Infinity;
    const campaignsCacheAge = lastCampaignsFetched ? Date.now() - lastCampaignsFetched : Infinity;
    const hasValidCoreCache = hasCoreData && lastCoreFetched && coreCacheAge < CACHE_TTL;
    const hasValidCampaignsCache =
      lastCampaignsFetched
      && campaignsCacheAge < CACHE_TTL
      && (dashboardState?.campaignAnalytics?.popularCampaigns?.length ?? 0) > 0;

    if (hasValidCoreCache) {
      hasInitialLoadRef.current = true;
      if (!hasValidCampaignsCache) {
        dispatch(fetchCampaignAnalytics({
          dateRange: backendDateRange,
          sections: 'campaigns',
          force: true,
        }));
        dispatch(fetchCampaignCTR({ dateRange: backendDateRange }));
      }
      return;
    }
    
    // ✅ İlk yüklemede sadece gerekli verileri çek (cache kontrolü Redux thunk'ta da yapılacak)
    fetchInitialDashboardData({ force: false }).then(() => {
      hasInitialLoadRef.current = true; // İlk yükleme tamamlandı olarak işaretle
    }).catch((error) => {
      hasInitialLoadRef.current = true; // Hata olsa bile işaretle (tekrar denememek için)
    });

    // ❌ KALDIRILDI: Periyodik REST API çağrıları
    // WebSocket dashboard:sync event'i zaten 30 saniyede bir fallback olarak gönderiliyor
    // Gereksiz API çağrıları performans sorununa neden oluyor

    // ❌ KALDIRILDI: visibilitychange event listener
    // Sayfa geri geldiğinde tüm verileri tekrar çekmeye gerek yok çünkü:
    // 1. WebSocket zaten 30 saniyede bir dashboard:sync gönderiyor
    // 2. ETag cache mekanizması var
    // 3. Gereksiz API çağrıları performans sorununa neden oluyor
    // ✅ Sadece canViewDashboard değiştiğinde çalışır (ilk yükleme)
    // ✅ fetchInitialDashboardData dependency'de DEĞİL çünkü sadece ilk yüklemede çalışmalı
    // ✅ dateRange değiştiğinde bu useEffect çalışmamalı (ayrı useEffect var)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewDashboard]);

  /**
   * Tarih aralığı değiştiğinde sadece gerekli verileri güncelle
   * KPI ve User Growth Chart verilerini yeniden çek
   * Diğer veriler WebSocket'ten gelecek
   * 
   * ✅ SADECE dateRange değiştiğinde çalışır
   * ✅ 30 saniyede bir ÇALIŞMAZ
   * ✅ Sayfa geri geldiğinde dateRange değişmemişse ÇALIŞMAZ
   */
  useEffect(() => {
    if (!canViewDashboard) {
      return;
    }

    // ✅ İlk yükleme henüz yapılmadıysa burada çalışmasın (ilk useEffect'te yapılacak)
    if (!hasInitialLoadRef.current) {
      return;
    }
    
    fetchInitialDashboardData({ force: true });
  }, [dateRange, canViewDashboard]);

  const toLocalHourLabel = (rawDate, fallbackLabel) => {
    if (!rawDate || typeof rawDate !== 'string') return fallbackLabel || 'N/A';

    // Backend artık +03:00 (veya timezone içeren) standart döndürüyor.
    // Timezone yoksa değeri local saat olarak yorumlarız.
    const normalized = rawDate.includes(' ') ? rawDate.replace(' ', 'T') : rawDate;
    const parsed = new Date(normalized);
    if (!isNaN(parsed.getTime())) {
      return parsed.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    return fallbackLabel || rawDate;
  };


  const userGrowthData = React.useMemo(() => {
    const growthSeries = Array.isArray(dashboardStats?.charts?.userGrowth)
      ? dashboardStats.charts.userGrowth
      : [];

    if (growthSeries.length === 0) return [];

    return growthSeries.map((item) => {
      const dateFull =
        dateRange === 'today'
          ? toLocalHourLabel(item.date, item.displayDate || item.label)
          : (item.displayDate || item.label || item.date || 'N/A');

      return {
        date: formatUserGrowthAxisLabel(dateFull, dateRange),
        dateFull,
        users: item.users || item.count || item.value || 0,
      };
    });
  }, [dashboardStats?.charts?.userGrowth, dateRange]);

  const userGrowthChartConfig = React.useMemo(() => {
    const pointCount = userGrowthData?.length || 0;
    const tickInterval =
      dateRange === 'yearly' && pointCount > 14
        ? Math.max(1, Math.floor(pointCount / 12))
        : dateRange === 'monthly' && pointCount > 16
          ? Math.max(1, Math.floor(pointCount / 10))
          : 0;

    if (dateRange === 'yearly') {
      return {
        margin: { top: 12, right: 20, bottom: 28, left: 8 },
        xAxis: {
          height: 48,
          angle: 0,
          textAnchor: 'middle',
          dy: 10,
          interval: tickInterval,
          minTickGap: 32,
          fontSize: 11,
        },
      };
    }

    if (dateRange === 'monthly') {
      return {
        margin: { top: 12, right: 16, bottom: 56, left: 8 },
        xAxis: {
          height: 64,
          angle: -35,
          textAnchor: 'end',
          dy: 8,
          interval: tickInterval,
          minTickGap: 24,
          fontSize: 11,
        },
      };
    }

    return {
      margin: { top: 10, right: 10, bottom: 48, left: 10 },
      xAxis: {
        height: 60,
        angle: -45,
        textAnchor: 'end',
        dy: 10,
        interval: 0,
        minTickGap: 16,
        fontSize: 12,
      },
    };
  }, [dateRange, userGrowthData?.length]);

  // En az bir pozitif büyüme değeri yoksa boş durum göster
  const hasUserGrowthData = React.useMemo(() => {
    if (!Array.isArray(userGrowthData) || userGrowthData.length === 0) return false;
    return userGrowthData.some((item) => Number(item?.users ?? 0) > 0);
  }, [userGrowthData]);

  const hasAgeDistributionData = React.useMemo(() => {
    const values = Object.values(ageGroups || {});
    return values.length > 0 && values.some((count) => Number(count) > 0);
  }, [ageGroups]);

  const userGrowthDebug = React.useMemo(() => {
    const values = (userGrowthData || []).map((item) => Number(item?.users || 0));
    const total = values.reduce((sum, v) => sum + v, 0);
    const max = values.length ? Math.max(...values) : 0;
    const last = values.length ? values[values.length - 1] : 0;
    return { total, max, last, points: values.length };
  }, [userGrowthData]);

  // Platform dağılımı verilerini hazırla (iOS/Android — Diğer hariç)
  const platformData = React.useMemo(() => {
    if (!platformDistribution) {
      return [
        { name: 'iOS', value: 0, percentage: '0', color: '#007AFF' },
        { name: 'Android', value: 0, percentage: '0', color: '#34C759' },
      ];
    }

    const ios = platformDistribution.ios || 0;
    const android = platformDistribution.android || 0;
    const knownTotal = ios + android;

    return [
      { name: 'iOS', value: ios, percentage: knownTotal > 0 ? ((ios / knownTotal) * 100).toFixed(1) : '0', color: '#007AFF' },
      { name: 'Android', value: android, percentage: knownTotal > 0 ? ((android / knownTotal) * 100).toFixed(1) : '0', color: '#34C759' },
    ];
  }, [platformDistribution]);

  const VERSION_COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#14B8A6'];

  const isOtherVersionLabel = (name) => {
    const normalized = String(name || '')
      .trim()
      .toLocaleLowerCase('tr-TR')
      .normalize('NFKC')
      .replace(/\u0307/g, '');
    return ['unknown', 'bilinmiyor', 'other', 'diğer', 'diger', 'n/a', 'na', ''].includes(normalized);
  };

  const appVersionData = React.useMemo(() => {
    const expectedRange = mapFrontendDateRangeToBackend(dateRange);
    const storedRange = appVersionDistribution?.dateRange || 'all';
    if (storedRange !== expectedRange) {
      return { items: [], total: 0, stale: true };
    }

    const versions = (appVersionDistribution?.versions || []).filter(
      (item) => !isOtherVersionLabel(item.version)
    );
    const knownTotal = versions.reduce((sum, item) => sum + (item.count || 0), 0);

    const items = versions.map((item, index) => ({
      name: item.version,
      value: item.count || 0,
      percentage: knownTotal > 0 ? (((item.count || 0) / knownTotal) * 100).toFixed(1) : '0',
      color: VERSION_COLORS[index % VERSION_COLORS.length],
    }));

    return { items, total: knownTotal, stale: false };
  }, [appVersionDistribution, dateRange]);

  // KPI kartları - API verilerinden dinamik
  const kpiCards = React.useMemo(() => [
    {
      icon: <Users size={24} />,
      label: 'Toplam Kullanıcı',
      // ✅ KPI endpoint'inden gelen totalUsers
      value: String((kpiTotalUsers || users?.length || 0).toLocaleString()),
      // ✅ KPI endpoint'inden gelen newUsers
      change: kpiNewUsers > 0 ? `+${String(kpiNewUsers)}` : '+0',
      positive: true,
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      loading: campaignAnalyticsLoading || usersLoading,
    },
    {
      icon: <Activity size={24} />,
      label: 'Aktif Kullanıcı', // ✅ Son 24 saatte aktivite yapan kullanıcılar
      value: String(activeUsers24Saat.toLocaleString()), // ✅ kpi.activeUsers - Son 24 saatte aktivite yapanlar
      change: null,
      positive: true,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      loading: campaignAnalyticsLoading, // ✅ KPI endpoint loading state'i
    },
    {
      icon: <Gift size={24} />,
      label: 'Toplam Oturum',
      // ✅ KPI endpoint'inden gelen sessions verisi
      value: String((kpiSessions || 0).toLocaleString()),
      change: null,
      positive: true,
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      loading: campaignAnalyticsLoading, // KPI endpoint'i loading state'i
    },
    {
      icon: <TrendingUp size={24} />,
      label: 'Kampanya Etkileşimi',
      value: String((campaignSummary.campaignInteractions || 0).toLocaleString()),
      change: null,
      positive: true,
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      loading: campaignCTRLoading || campaignSectionsLoading || campaignAnalyticsLoading,
    },
  ], [
    kpiTotalUsers, // ✅ KPI endpoint'inden gelen totalUsers
    users?.length, // Fallback
    campaignAnalyticsLoading, // ✅ KPI endpoint loading
    usersLoading,
    kpiNewUsers, // ✅ KPI endpoint'inden gelen newUsers
    newUsersCount, // ✅ Seçilen tarih aralığına göre hesaplanan yeni kullanıcı sayısı
    dateRange, // ✅ dateRange değiştiğinde yeniden hesapla
    kpiSessions, // ✅ KPI endpoint'inden gelen sessions
    dashboardStats?.kpi?.sessions, // ✅ Backend'den gelen session verisi (fallback)
    dashboardStats?.sessions, // Eski yapıyla uyumluluk için (deprecated)
    activeUsers24Saat, // ✅ Son 24 saatte aktivite yapan kullanıcılar (kpi.activeUsers)
    campaignAnalytics?.activeUsers,
    kpiData.activeUsers,
    campaignInteractionCount, // ✅ Kampanya etkileşimi (sadece kampanya detail view'ları)
    campaignSummary.campaignInteractions,
    campaignCTR?.total?.detailViews, // ✅ CTR'den kampanya detail görüntüleme sayısı
    campaignCTRLoading,
    campaignSectionsLoading,
    campaignSummary.totalCampaigns,
    campaignAnalyticsLoading
  ]);

  const dateRangeOptions = [
    { value: 'today', label: 'Bugün' },
    { value: 'weekly', label: 'Son 7 gün' },
    { value: 'monthly', label: 'Son 30 gün' },
  ];

  const getCurrentDateRangeLabel = () => {
    const option = dateRangeOptions.find(opt => opt.value === dateRange);
    return option ? option.label : 'Son 30 gün';
  };

  /**
   * Manuel yenileme fonksiyonu
   * Sadece gerekli verileri force ile yeniden çeker
   */
  const handleRefresh = async () => {
    await fetchInitialDashboardData({ force: true });
  };

  // Helper functions for Activity Feed
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Az önce';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} dk önce`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} saat önce`;
    return `${Math.floor(diffInSeconds / 86400)} gün önce`;
  };

  const getEventIcon = (category) => {
    switch (category) {
      case 'Ödeme': return <CreditCard className="w-5 h-5 text-purple-600" />;
      case 'Kayıt': return <UserPlus className="w-5 h-5 text-blue-600" />;
      case 'Kampanya': return <Gift className="w-5 h-5 text-pink-600" />;
      case 'Hata': return <ShieldAlert className="w-5 h-5 text-red-600" />;
      case 'Sayfa': return <FileText className="w-5 h-5 text-indigo-600" />;
      case 'Limit': return <AlertCircle className="w-5 h-5 text-orange-600" />;
      default: return <Activity className="w-5 h-5 text-gray-500" />;
    }
  };

  const getEventBackground = (category) => {
    switch (category) {
      case 'Ödeme': return 'bg-purple-50';
      case 'Kayıt': return 'bg-blue-50';
      case 'Kampanya': return 'bg-pink-50';
      case 'Hata': return 'bg-red-50';
      case 'Sayfa': return 'bg-indigo-50';
      case 'Limit': return 'bg-orange-50';
      default: return 'bg-gray-50';
    }
  };

  // Dashboard izni yoksa kullanıcıyı uygun sayfaya yönlendir
  if (!canViewDashboard) {
    const defaultPage = getDefaultPageForUser(user);
    return <Navigate to={defaultPage} replace />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      {/* Header */}
      <div className="bg-white rounded-2xl p-5 mb-5 border border-gray-100 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Hoş Geldiniz</h1>
            <p className="text-gray-500 mt-2 text-base">
              Bugünkü sadakat programı verileriniz hazır.
            </p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
              <Clock size={14} />
              <span>Son güncelleme: {lastUpdate.toLocaleTimeString('tr-TR')}</span>
            </div>
            {/* Anlık Aktif Kullanıcı Sayısı */}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full border border-green-100">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-green-700">Anlık Aktif:</span>
                {(() => {
                  // ✅ Anlık aktif kullanıcı: Sadece WebSocket'ten gelen realtime.activeUsers
                  // Bu değer şu anda aktif olan (bağlı olan) kullanıcı sayısını gösterir
                  const anlikAktif = realtimeData?.activeUsers ?? 0;
                  
                  
                  return realtimeLoading ? (
                    <div className="h-4 w-8 bg-green-100 rounded animate-pulse"></div>
                  ) : (
                    <span className="text-sm font-bold text-green-900">
                      {String(anlikAktif.toLocaleString('tr-TR'))} kullanıcı
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Yenile Butonu */}
            {canViewDashboard && (
              <button
                onClick={handleRefresh}
                className="group flex items-center justify-center w-10 h-10 rounded-full bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300"
                title="Yenile"
              >
                <RefreshCw size={20} className={`transform group-hover:rotate-180 transition-transform duration-500`} />
              </button>
            )}

            {/* Date Range Filter */}
            <Dropdown
              iconLeft={<Calendar size={18} />}
              items={dateRangeOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
              value={dateRange}
              onChange={setDateRange}
              simple
              className="min-w-[200px]"
            />
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card, index) => (
          <div
            key={index}
            className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 relative overflow-hidden"
          >
            <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${card.bgColor.replace('bg-', 'from-')} to-white opacity-20 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110 duration-500`} />

            <div className="relative z-10">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-14 h-14 rounded-2xl ${card.bgColor} ${card.iconColor} flex items-center justify-center shadow-inner`}>
                  {card.icon}
                </div>
                {card.change && (
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${card.positive ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'} flex items-center gap-1`}>
                  {card.positive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {card.change}
                </div>
                )}
              </div>

              <p className="text-sm font-medium text-gray-500 mb-1">{card.label}</p>

              {card.loading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-100 rounded mb-2 w-1/2"></div>
                </div>
              ) : (
                <h3 className="text-2xl font-bold text-gray-900 tracking-tight">
                  {card.value}
                </h3>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* User Growth Chart */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Kullanıcı Büyümesi</h3>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <TrendingUp className="text-blue-600" size={20} />
            </div>
          </div>

          {campaignAnalyticsLoading && !hasUserGrowthData ? (
            <div className="flex items-center justify-center h-[300px]">
              <Lottie animationData={loadingAnimation} loop={true} className="w-32 h-32" />
            </div>
          ) : !hasUserGrowthData ? (
            <div className="flex flex-col items-center justify-center h-[400px] text-center">
              <div className="p-4 bg-gray-50 rounded-full mb-4">
                <TrendingUp className="text-gray-400" size={32} />
              </div>
              <h4 className="text-lg font-semibold text-gray-700 mb-2">Henüz kullanıcı büyümesi verisi yok</h4>
              <p className="text-sm text-gray-500 max-w-md">
                Seçili tarih aralığında kullanıcı büyümesi kaydı bulunmamaktadır.
              </p>
            </div>
          ) : (
            <div className="w-full min-h-[400px]">
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart
                data={userGrowthData}
                margin={userGrowthChartConfig.margin}
              >
                <defs>
                  <linearGradient id="userGrowthGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                    <stop offset="50%" stopColor="#3b82f6" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="userGrowthStroke" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="100%" stopColor="#2563eb" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: userGrowthChartConfig.xAxis.fontSize }}
                  dy={userGrowthChartConfig.xAxis.dy}
                  padding={{ left: 20, right: 20 }}
                  angle={userGrowthChartConfig.xAxis.angle}
                  textAnchor={userGrowthChartConfig.xAxis.textAnchor}
                  height={userGrowthChartConfig.xAxis.height}
                  interval={userGrowthChartConfig.xAxis.interval}
                  minTickGap={userGrowthChartConfig.xAxis.minTickGap}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                  dx={-10}
                  width={50}
                  padding={{ top: 10, bottom: 10 }}
                  allowDecimals={false}
                  tickFormatter={(value) => Math.round(value)}
                  domain={[0, 'dataMax']}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    padding: '12px 16px'
                  }}
                  itemStyle={{ color: '#1e293b', fontWeight: 600 }}
                  labelStyle={{ color: '#64748b', marginBottom: '4px', fontSize: '12px' }}
                  labelFormatter={(_label, payload) =>
                    payload?.[0]?.payload?.dateFull || _label
                  }
                  cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }}
                />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke="url(#userGrowthStroke)"
                  strokeWidth={3}
                  fill="url(#userGrowthGradient)"
                  fillOpacity={1}
                  dot={false}
                  activeDot={{ r: 6, fill: '#2563eb', stroke: 'white', strokeWidth: 3 }}
                />
              </AreaChart>
            </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Campaign Performance & CTR Analysis - Combined */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Kampanya Performansı & CTR Analizi</h3>
              <p className="text-sm text-gray-500 mt-1">Genel analiz ve detaylı kampanya listesi</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/dashboard/campaigns/all', {
                  state: { dateRange: mapDashboardDateRangeToStats(dateRange) },
                })}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
              >
                Tümünü Gör
                <ArrowUpRight size={16} />
              </button>
            <div className="p-3 bg-green-50 rounded-xl">
              <Activity className="text-green-600" size={20} />
              </div>
            </div>
          </div>

        {(campaignAnalyticsLoading) ? (
            <div className="flex items-center justify-center h-[300px]">
              <Lottie animationData={loadingAnimation} loop={true} className="w-32 h-32" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Genel CTR Tablosu */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wider">Genel Kampanya CTR Analizi</h4>
                {campaignCTRLoading && !campaignCTR ? (
                  <div className="flex items-center justify-center h-[160px]">
                    <Lottie animationData={loadingAnimation} loop={true} className="w-24 h-24" />
                  </div>
                ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Kampanya Tipi</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Görüntülenme</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tıklama</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">CTR (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-3">
                            <span className="flex w-12 shrink-0 items-center justify-center">
                              <CampaignTypeBadge personalized={false} />
                            </span>
                            <span className="font-medium text-gray-900">Genel</span>
                          </div>
                        </td>
                        <td className="text-right py-2.5 px-3 text-gray-700 font-medium">{campaignCTR?.general?.impressions || 0}</td>
                        <td className="text-right py-2.5 px-3 text-gray-700 font-medium">{campaignCTR?.general?.detailViews || 0}</td>
                        <td className="text-right py-2.5 px-3">
                          <span className="font-bold text-blue-600">{(campaignCTR?.general?.ctr || 0).toFixed(2)}%</span>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-3">
                            <span className="flex w-12 shrink-0 items-center justify-center">
                              <CampaignTypeBadge personalized={true} />
                            </span>
                            <span className="font-medium text-gray-900">Bana Özel</span>
                          </div>
                        </td>
                        <td className="text-right py-2.5 px-3 text-gray-700 font-medium">{campaignCTR?.personalized?.impressions || 0}</td>
                        <td className="text-right py-2.5 px-3 text-gray-700 font-medium">{campaignCTR?.personalized?.detailViews || 0}</td>
                        <td className="text-right py-2.5 px-3">
                          <span className="font-bold text-purple-600">{(campaignCTR?.personalized?.ctr || 0).toFixed(2)}%</span>
                        </td>
                      </tr>
                      <tr className="border-b-2 border-gray-200 hover:bg-indigo-50 transition-colors bg-indigo-50/30">
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-3">
                            <span className="flex w-12 shrink-0 items-center justify-center">
                              <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-full bg-indigo-100 px-2.5 py-1 text-sm font-bold text-indigo-700 ring-1 ring-indigo-200">
                                T
                              </span>
                            </span>
                            <span className="font-bold text-gray-900">Toplam</span>
                          </div>
                        </td>
                        <td className="text-right py-2.5 px-3 text-gray-900 font-bold">{campaignCTR?.total?.impressions || 0}</td>
                        <td className="text-right py-2.5 px-3 text-gray-900 font-bold">{campaignCTR?.total?.detailViews || 0}</td>
                        <td className="text-right py-2.5 px-3">
                          <span className="font-bold text-indigo-600">{(campaignCTR?.total?.ctr || 0).toFixed(2)}%</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                )}
              </div>

              {/* Detaylı Kampanya Listesi */}
              {((!campaignSectionsSettled || campaignSectionsLoading) && popularCampaigns.length === 0) ? (
                <div className="flex items-center justify-center h-[200px]">
                  <Lottie animationData={loadingAnimation} loop={true} className="w-24 h-24" />
                </div>
              ) : popularCampaigns.length > 0 ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wider">Kampanya Performansı (İlk 5 Kampanya)</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Kampanya Adı</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Görüntülenme</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tıklama</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">CTR (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {popularCampaigns.map((campaign, index) => {
                          const personalized = resolveCampaignPersonalized(campaign);
                          return (
                          <tr
                            key={campaign.campaignId || index}
                            className={`border-b border-gray-100 transition-colors border-l-4 ${
                              personalized
                                ? 'border-l-violet-500 bg-violet-50/70 hover:bg-violet-50'
                                : 'border-l-transparent hover:bg-gray-50'
                            }`}
                          >
                            <td className="py-2.5 px-3">
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                                <CampaignTypeBadge campaign={campaign} />
                                <span className="font-medium text-gray-900">
                                  {campaign.campaignName || campaign.campaignTitle || 'İsimsiz Kampanya'}
                                </span>
                              </div>
                            </td>
                            <td className="text-right py-2.5 px-3 text-gray-700 font-medium">{campaign.impressions || campaign.views || 0}</td>
                            <td className="text-right py-2.5 px-3 text-gray-700 font-medium">{campaign.clicks || campaign.detailViews || 0}</td>
                            <td className="text-right py-2.5 px-3">
                              <span className={`font-bold ${personalized ? 'text-violet-700' : 'text-green-600'}`}>
                                {(campaign.ctr || 0).toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
              <Gift size={48} className="mb-4 text-gray-200" />
                  <p>Henüz detaylı kampanya verisi yok</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Advanced Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Session Duration Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-teal-50 rounded-lg text-teal-600">
              <Clock size={20} />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Oturum Süresi Analizi</h3>
          </div>

          {userBehaviorLoading ? (
            <div className="flex items-center justify-center h-[200px]">
              <Lottie animationData={loadingAnimation} loop={true} className="w-24 h-24" />
            </div>
          ) : userBehavior?.sessionDuration ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="relative mx-auto sm:mx-0 w-28 h-28 sm:w-32 sm:h-32 shrink-0 flex items-center justify-center">
                <svg viewBox="0 0 200 200" className="w-full h-full transform -rotate-90">
                  <circle cx="100" cy="100" r="82" className="text-gray-100" strokeWidth="10" fill="none" stroke="currentColor" />
                  <circle
                    cx="100"
                    cy="100"
                    r="82"
                    className="text-teal-500"
                    strokeWidth="10"
                    fill="none"
                    stroke="currentColor"
                    strokeDasharray="515.2"
                    strokeDashoffset={515.2 - (515.2 * 75) / 100}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-lg font-bold text-gray-900">{String(userBehavior.sessionDuration)}s</span>
                  <span className="text-xs text-gray-500 font-medium">Ortalama</span>
                </div>
              </div>

              <div className="flex-1 w-full sm:pl-2 space-y-3">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-500 font-medium">Toplam Etkileşim</span>
                    <span className="text-sm font-bold text-gray-900">{String(userBehavior.totalEvents || 0)}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="bg-teal-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-500 font-medium">Benzersiz Ziyaretçi</span>
                    <span className="text-sm font-bold text-gray-900">{String(userBehavior.uniqueUsers || 0)}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: '60%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <Activity size={32} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm">Oturum verisi bulunamadı</p>
            </div>
          )}
        </div>

        {/* Platform Distribution Chart */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
              <CreditCard size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Platform Dağılımı</h3>
              <p className="text-xs text-gray-500 mt-1">iOS / Android</p>
            </div>
          </div>
        </div>

        {(campaignAnalyticsLoading && !platformDistribution) ? (
          <div className="flex items-center justify-center h-[250px]">
            <Lottie animationData={loadingAnimation} loop={true} className="w-24 h-24" />
          </div>
        ) : platformData.some(item => item.value > 0) ? (
          <div className="space-y-4">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={false}
                  outerRadius={70}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {platformData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    padding: '8px 12px'
                  }}
                  formatter={(value, name) => [`${value} kullanıcı`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 pt-2 border-t border-gray-100">
              {platformData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm font-medium text-gray-700">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">{item.value.toLocaleString('tr-TR')}</span>
                    <span className="text-xs font-semibold text-gray-500 w-12 text-right">{item.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[250px] text-gray-400">
            <CreditCard size={48} className="mb-4 text-gray-200" />
            <p className="text-sm">Henüz platform verisi yok</p>
          </div>
        )}
        </div>

        {/* App Version Distribution */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                <Smartphone size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Versiyon Dağılımı</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {getCurrentDateRangeLabel()} · kullanıcı raporu
                </p>
              </div>
            </div>
          </div>

          {(campaignAnalyticsLoading && !appVersionDistribution) || appVersionData.stale ? (
            <div className="flex items-center justify-center h-[250px]">
              <Lottie animationData={loadingAnimation} loop={true} className="w-24 h-24" />
            </div>
          ) : appVersionData.items.some(item => item.value > 0) ? (
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <Pie
                    data={appVersionData.items}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {appVersionData.items.map((entry, index) => (
                      <Cell key={`version-cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      padding: '8px 12px'
                    }}
                    formatter={(value, name) => [`${value} kullanıcı`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 pt-2 border-t border-gray-100">
                {appVersionData.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm font-medium text-gray-700">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">{item.value.toLocaleString('tr-TR')}</span>
                      <span className="text-xs font-semibold text-gray-500 w-12 text-right">{item.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[250px] text-gray-400">
              <Smartphone size={48} className="mb-4 text-gray-200" />
              <p className="text-sm">Henüz versiyon verisi yok</p>
            </div>
          )}
        </div>
      </div>

      {/* Activity Feed ve Statistics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Activity Feed */}
        <ActivityFeed
          dateFrom={activityFeedDateFilter.dateFrom}
          dateTo={activityFeedDateFilter.dateTo}
        />

        {/* Activity Statistics Widget */}
        <ActivityStatsWidget dateRange={dateRange} />
      </div>

      {/* Demografik İstatistikler - Yaş ve Cinsiyet */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Yaş Aralıkları */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
              <Calendar size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Yaş Dağılımı</h3>
              <p className="text-sm text-gray-500 mt-1">Kullanıcıların yaş aralıkları</p>
            </div>
          </div>

          {demographicsLoading ? (
            <div className="flex items-center justify-center h-[200px]">
              <Lottie animationData={loadingAnimation} loop={true} className="w-24 h-24" />
            </div>
          ) : hasAgeDistributionData ? (
            <div className="space-y-4">
              {Object.entries(ageGroups)
                .filter(([, count]) => Number(count) > 0)
                .map(([ageRange, count]) => {
                const total = Object.values(ageGroups).reduce((sum, val) => sum + val, 0);
                const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                const ageLabel = (ageRange === '18-' || ageRange === '<18') ? '18 Yaş Altı' : 
                                 ageRange === '56+' ? '56 Yaş Üstü' : 
                                 `${ageRange} Yaş`;
                
                return (
                  <div key={ageRange} className="p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-all duration-300">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-900">{ageLabel}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">{count} kullanıcı</span>
                        <span className="text-lg font-bold text-blue-600">{percentage}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
              <Calendar size={48} className="mb-4 text-gray-200" />
              <p className="text-sm">Henüz yaş dağılımı verisi yok</p>
            </div>
          )}
        </div>

        {/* Cinsiyet Dağılımı */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-pink-50 rounded-lg text-pink-600">
              <UserCog size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Cinsiyet Dağılımı</h3>
              <p className="text-sm text-gray-500 mt-1">Kullanıcıların cinsiyet dağılımı</p>
            </div>
          </div>

          {demographicsLoading ? (
            <div className="flex items-center justify-center h-[200px]">
              <Lottie animationData={loadingAnimation} loop={true} className="w-24 h-24" />
            </div>
          ) : Object.keys(genderStats).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(genderStats).map(([gender, count]) => {
                const total = Object.values(genderStats).reduce((sum, val) => sum + val, 0);
                const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                const genderColor = gender === 'Erkek' ? 'from-blue-500 to-blue-600' : 'from-pink-500 to-pink-600';
                const genderBgColor = gender === 'Erkek' ? 'bg-blue-50' : 'bg-pink-50';
                
                return (
                  <div key={gender} className={`p-4 ${genderBgColor} rounded-2xl hover:opacity-90 transition-all duration-300`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-900">{gender}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">{count} kullanıcı</span>
                        <span className="text-lg font-bold text-gray-900">{percentage}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className={`bg-gradient-to-r ${genderColor} h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
              <UserCog size={48} className="mb-4 text-gray-200" />
              <p className="text-sm">Henüz cinsiyet verisi yok</p>
            </div>
          )}
        </div>
      </div>

      {/* Error States Summary */}
      {(realtimeError || campaignAnalyticsError || userBehaviorError || usersError) && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-4">⚠️ Veri Yükleme Hataları</h3>
          <div className="space-y-2">
            {realtimeError && (
              <p className="text-sm text-red-700">• Real-time Veriler: {getErrorMessage(realtimeError)}</p>
            )}
            {campaignAnalyticsError && (
              <p className="text-sm text-red-700">• Kampanya Analizleri: {getErrorMessage(campaignAnalyticsError)}</p>
            )}
            {userBehaviorError && (
              <p className="text-sm text-red-700">• Kullanıcı Davranışı: {getErrorMessage(userBehaviorError)}</p>
            )}
            {usersError && (
              <p className="text-sm text-red-700">• Kullanıcı Listesi: {getErrorMessage(usersError)}</p>
            )}
          </div>
        </div>
      )}

      {/* Son Yorumlar */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm mt-8">
        <div 
          className="flex items-center justify-between mb-6 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/kullanici-yorumlari')}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg text-yellow-600">
              <MessageSquare size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Son Yorumlar</h3>
              <p className="text-sm text-gray-500 mt-1">Kullanıcı değerlendirmeleri</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
            <span>Tümünü Gör</span>
            <ArrowUpRight size={16} />
          </div>
        </div>

        {ratingsLoading && (!ratings || ratings.length === 0) ? (
          <div className="flex items-center justify-center h-[200px]">
            <Lottie animationData={loadingAnimation} loop={true} className="w-24 h-24" />
          </div>
        ) : ratings && ratings.length > 0 ? (
          <div className="space-y-4">
            {ratings.map((rating) => {
              // 3 yıldız ve altı için açık kırmızı/pembe arka plan
              const isLowRating = rating.point <= 3;
              const displayName = getRatingDisplayName(rating);
              const routeId = getRatingRouteId(rating);
              return (
              <div
                key={rating.id}
                className={`p-4 rounded-2xl transition-all duration-300 ${
                  isLowRating 
                    ? 'bg-red-50 hover:bg-red-100 border border-red-100' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {routeId ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/kullanicilar/${routeId}`)}
                        className="text-base font-semibold text-gray-900 hover:text-black hover:underline"
                      >
                        {displayName}
                      </button>
                    ) : (
                      <span className="text-base font-semibold text-gray-900">
                        {displayName}
                      </span>
                    )}
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          className={i < rating.point ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                        />
                      ))}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(rating.createdAt || rating.createdDate).toLocaleDateString('tr-TR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                {rating.comment && (
                  <p className="text-sm text-gray-700 line-clamp-2">{rating.comment}</p>
                )}
              </div>
            );
            })}
          </div>
        ) : ratingsLoading ? (
          <div className="flex items-center justify-center h-[200px]">
            <Lottie animationData={loadingAnimation} loop={true} className="w-24 h-24" />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[200px] text-gray-400">
            <MessageSquare size={48} className="mb-4 text-gray-200" />
            <p className="text-sm">Henüz yorum yok</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;

